/**
 * API client for the recordings that sit on Twilio's side rather than ours: the
 * storage audit, the purge, and the ones no call log row claims.
 *
 * Separate from `lib/calls.ts` on purpose - none of this is about a call an
 * operator made, it is about what a month of calls left behind and what it costs
 * to keep. Read by the Settings storage cards, not by the call log.
 */
import { api } from "./api";

// ── Twilio recording storage ────────────────────────────────────────────────
// Twilio bills recording storage monthly for as long as a file sits in the
// account. We download every recording and delete Twilio's copy, but that delete
// is best-effort, so leftovers accumulate. This is the audit and the cleanup.

/**
 * What one Twilio-side recording is to us:
 *  redundant     - we hold our own copy, Twilio's is billed for nothing
 *  in_flight     - the download is not finished; leave it alone
 *  stale_failed  - our side gave up and Twilio still has the file; recoverable
 *  missing_local - our object is gone, Twilio's is the last copy left
 *  unknown       - no call log row carries this recording
 *
 * `stale_failed` is the only kind here that can be REPAIRED. It used to be
 * counted as `in_flight`, which says "something is working on it" about a row
 * the sweep abandoned a day ago - so a recording Twilio still held sat in a
 * bucket the operator had been taught to ignore. The call pane's retry is what
 * clears it.
 */
export type OrphanKind =
  | "redundant"
  | "in_flight"
  | "stale_failed"
  | "missing_local"
  | "unknown";

export interface OrphanItem {
  sid: string;
  callSid: string | null;
  durationSec: number;
  createdAt: string;
  kind: OrphanKind;
  callLogId: string | null;
}

export interface OrphanReport {
  scanned: number;
  byKind: Record<OrphanKind, { count: number; minutes: number }>;
  redundantMonthlyUsd: number;
  totalMonthlyUsd: number;
  truncated: boolean;
  /** False = our own copies were not checked in storage, the row was trusted. */
  verified: boolean;
  items: OrphanItem[];
}

export interface OrphanPurgeResult {
  deleted: number;
  failed: number;
  remaining: number;
  savedMonthlyUsd: number;
}

export function getOrphanReport(opts: { limit?: number; verify?: boolean } = {}): Promise<OrphanReport> {
  const qs = new URLSearchParams();
  if (opts.limit) qs.set("limit", String(opts.limit));
  if (opts.verify) qs.set("verify", "1");
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return api.get(`/admin/crm/calls/recordings/orphans${suffix}`);
}

export function purgeOrphanRecordings(limit?: number): Promise<OrphanPurgeResult> {
  return api.post("/admin/crm/calls/recordings/orphans/purge", { confirm: true, ...(limit ? { limit } : {}) });
}

// ── Unknown recordings ──────────────────────────────────────────────────────
// The bucket the automatic purge never touches: Twilio holds them, no call log
// row claims them, and we have no copy. Listing, playback and a manual delete.

/** The parent call as Twilio still knows it; null once Twilio has aged it out. */
export interface UnknownCallDetail {
  from: string | null;
  to: string | null;
  direction: string | null;
  status: string | null;
  startedAt: string | null;
}

export interface UnknownRecording {
  sid: string;
  callSid: string | null;
  durationSec: number;
  createdAt: string;
  call: UnknownCallDetail | null;
}

export interface UnknownListing {
  total: number;
  scanned: number;
  truncated: boolean;
  /** Every unknown sid in the scan window, so "select all" covers more than the page. */
  allSids: string[];
  offset: number;
  items: UnknownRecording[];
  monthlyUsd: number;
}

export interface UnknownDeleteResult {
  deleted: number;
  failed: number;
  /** Left alone: a call log row claims them now, or the scan did not reach them. */
  skipped: number;
  savedMonthlyUsd: number;
}

export function listUnknownRecordings(
  opts: { offset?: number; pageSize?: number; detail?: boolean } = {},
): Promise<UnknownListing> {
  const qs = new URLSearchParams();
  if (opts.offset) qs.set("offset", String(opts.offset));
  if (opts.pageSize) qs.set("pageSize", String(opts.pageSize));
  if (opts.detail) qs.set("detail", "1");
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return api.get(`/admin/crm/calls/recordings/orphans/unknown${suffix}`);
}

export function deleteUnknownRecordings(sids: string[]): Promise<UnknownDeleteResult> {
  return api.post("/admin/crm/calls/recordings/orphans/unknown/delete", { confirm: true, sids });
}

/** Same-origin, cookie-authed - usable straight as an <audio src> or download href. */
export function unknownRecordingAudioUrl(sid: string): string {
  return `/api/admin/crm/calls/recordings/orphans/${encodeURIComponent(sid)}/audio`;
}
